import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { BaseListLazyLoadEvent } from 'src/app/shared/components/base-list/models/base-list-event.model';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { InboundNumberService } from '../../services/inbound-number.service';
import { InboundNumberDto } from '../../models/inbound-number.dto';
import { InboundNumberFormComponent } from '../inbound-number-form/inbound-number-form.component';
import { TelecomProvider } from '../../models/telecom-provider.enum';
import { DIDRoutingType } from '../../models/did-routing-type.enum';

type InboundTplContext = BaseListColumnTemplateContext<InboundNumberDto>;

@Component({
  selector: 'app-inbound-numbers-list',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    TagModule,
    TooltipModule,
    ActionMenuComponent,
    BaseListComponent,
    InboundNumberFormComponent,
  ],
  templateUrl: './inbound-numbers-list.component.html',
  styleUrl: './inbound-numbers-list.component.scss',
})
export class InboundNumbersListComponent implements AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly inboundNumberService = inject(InboundNumberService);
  private readonly toaster = inject(ToasterService);
  private readonly confirmationService = inject(ConfirmationService);

  @ViewChild('numberTpl') numberTpl!: TemplateRef<InboundTplContext>;
  @ViewChild('routingTpl') routingTpl!: TemplateRef<InboundTplContext>;
  @ViewChild('ivrTpl') ivrTpl!: TemplateRef<InboundTplContext>;
  @ViewChild('statusTpl') statusTpl!: TemplateRef<InboundTplContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<InboundTplContext>;

  @Output() test = new EventEmitter<InboundNumberDto>();

  items: InboundNumberDto[] = [];
  loading = true;
  listConfig!: BaseListConfig<InboundNumberDto>;

  totalRecords = 0;
  skipCount = 0;
  rows = 10;

  showForm = false;
  isEditMode = false;
  selectedItem: InboundNumberDto | null = null;

  ngAfterViewInit(): void {
    const l = (key: string): string => this.localization.instant(key);

    const columns: BaseListColumn<InboundNumberDto>[] = [
      {
        field: 'phoneNumber',
        header: l('Settings::PhoneNumbers:NumberProvider'),
        bodyTemplate: this.numberTpl,
      },
      {
        field: 'routingType',
        header: l('Settings::PhoneNumbers:Routing'),
        bodyTemplate: this.routingTpl,
      },
      {
        field: 'ivrInstanceName',
        header: l('Settings::PhoneNumbers:IvrInstanceExtension'),
        bodyTemplate: this.ivrTpl,
      },
      {
        field: 'isActive',
        header: l('Settings::PhoneNumbers:Status'),
        width: '120px',
        bodyTemplate: this.statusTpl,
      },
      {
        field: 'id',
        header: l('Settings::PhoneNumbers:Actions'),
        width: '80px',
        bodyTemplate: this.actionsTpl,
      },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
      emptyMessage: l('Settings::PhoneNumbers:NoInboundNumbersFound'),
    };

    this.cdr.detectChanges();
  }

  onLazyLoad(event: BaseListLazyLoadEvent): void {
    this.skipCount = event.skipCount;
    this.rows = event.maxResultCount;
    this.loadList();
  }

  loadList(): void {
    this.loading = true;
    this.inboundNumberService.getList(this.skipCount, this.rows).subscribe({
      next: response => {
        this.items = response.items ?? [];
        this.totalRecords = response.totalCount ?? 0;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  isActive(row: InboundNumberDto): boolean {
    return row.isActive !== false;
  }

  getProviderLabel(provider: TelecomProvider): string {
    return this.localization.instant(`Settings::PhoneNumbers:Provider:${TelecomProvider[provider]}`);
  }

  getRoutingLabel(routing: DIDRoutingType): string {
    return this.localization.instant(`Settings::PhoneNumbers:Routing:${DIDRoutingType[routing]}`);
  }

  getExtensionShort(extension?: string): string {
    if (!extension) {
      return '—';
    }
    return extension.length > 3 ? `${extension.slice(0, 3)}…` : extension;
  }

  copyExtension(extension?: string): void {
    if (!extension || !navigator?.clipboard) {
      return;
    }
    navigator.clipboard.writeText(extension).then(() => {
      this.toaster.success('Settings::PhoneNumbers:ExtensionCopied');
    });
  }

  getMenuItems(row: InboundNumberDto): ActionMenuItem[] {
    const active = this.isActive(row);
    return [
      {
        label: this.localization.instant('Settings::PhoneNumbers:Edit'),
        icon: 'pi pi-pencil',
        action: () => this.openEdit(row),
      },
      {
        label: this.localization.instant('Settings::PhoneNumbers:TestConnectivity'),
        icon: 'pi pi-play',
        action: () => this.test.emit(row),
      },
      {
        label: this.localization.instant(
          active ? 'Settings::PhoneNumbers:Deactivate' : 'Settings::PhoneNumbers:Activate',
        ),
        icon: 'pi pi-power-off',
        action: () => (active ? this.deactivate(row) : this.activate(row)),
      },
      {
        label: this.localization.instant('Settings::PhoneNumbers:Remove'),
        icon: 'pi pi-trash',
        severity: 'danger',
        action: () => this.remove(row),
      },
    ];
  }

  openAdd(): void {
    this.selectedItem = null;
    this.isEditMode = false;
    this.showForm = true;
  }

  openEdit(row: InboundNumberDto): void {
    this.selectedItem = row;
    this.isEditMode = true;
    this.showForm = true;
  }

  onFormVisibilityChange(visible: boolean): void {
    this.showForm = visible;
    if (!visible) {
      this.selectedItem = null;
    }
  }

  onFormSaved(isCreate: boolean): void {
    this.showForm = false;
    this.selectedItem = null;
    this.toaster.success('Settings::PhoneNumbers:SuccessfullySaved');
    this.loadList();
  }

  private activate(row: InboundNumberDto): void {
    if (row.id == null) {
      return;
    }
    this.inboundNumberService.activate(row.id).subscribe({
      next: () => {
        this.toaster.success('Settings::PhoneNumbers:SuccessfullySaved');
        this.loadList();
      },
    });
  }

  private deactivate(row: InboundNumberDto): void {
    if (row.id == null) {
      return;
    }
    this.inboundNumberService.deactivate(row.id).subscribe({
      next: () => {
        this.toaster.success('Settings::PhoneNumbers:SuccessfullySaved');
        this.loadList();
      },
    });
  }

  private remove(row: InboundNumberDto): void {
    if (row.id == null) {
      return;
    }
    this.confirmationService
      .warn(
        'Settings::PhoneNumbers:ConfirmRemoveInbound',
        'Settings::PhoneNumbers:ConfirmRemoveTitle',
        {
          yesText: 'Settings::PhoneNumbers:Remove',
          cancelText: 'Settings::PhoneNumbers:Cancel',
        },
      )
      .pipe(
        switchMap(status =>
          status === Confirmation.Status.confirm
            ? this.inboundNumberService.delete(row.id!).pipe(map(() => true))
            : of(false),
        ),
      )
      .subscribe({
        next: done => {
          if (done) {
            this.toaster.success('Settings::PhoneNumbers:SuccessfullyDeleted');
            this.reloadAfterDelete();
          }
        },
      });
  }

  private reloadAfterDelete(): void {
    if (this.items.length <= 1 && this.skipCount >= this.rows) {
      this.skipCount -= this.rows;
    }
    this.loadList();
  }
}
